/**
 * i18n — lightweight pass-through translation module.
 *
 * English keys are used as-is (the key IS the English text).
 * Additional languages can be registered at runtime via `registerLocale()`.
 */

export type TranslateFn = (key: string) => string;

// ── Locale registry ────────────────────────────────
const localeRegistry: Record<string, Record<string, string>> = {};

export function registerLocale(code: string, translations: Record<string, string>): void {
  localeRegistry[code] = { ...(localeRegistry[code] || {}), ...translations };
}

/**
 * Create a translate function for a given locale.
 * For "en" (or when no locale pack is registered), the key itself is the English text.
 */
export function createT(locale?: string): TranslateFn {
  const code = locale?.toLowerCase().split("-")[0] || "en";
  return (key: string): string => {
    if (code === "en" || !localeRegistry[code]) return key;
    return localeRegistry[code][key] ?? key;
  };
}

/**
 * Detect the best locale from the browser environment.
 */
export function detectLanguage(): string {
  if (typeof navigator === "undefined") return "en";
  const lang = navigator.language || (navigator as any).userLanguage || "en";
  return lang.split("-")[0].toLowerCase();
}

// ── Supported locales ──────────────────────────────
// Trimmed to the 10 highest-impact languages for global fashion
// commerce. Drop the rest to reduce bundle size + maintenance burden.
export const SUPPORTED_LOCALES: string[] = [
  "en", "es", "fr", "de", "it", "pt", "ja", "zh", "ko", "ar",
];

export const LOCALE_LABELS: Record<string, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
  ja: "日本語",
  zh: "中文",
  ko: "한국어",
  ar: "العربية",
};

// ── Translation keys ───────────────────────────────
// All translatable UI strings used in the React component.
export const TRANSLATION_KEYS: string[] = [
  "Virtual Try-On",
  "Find Your Size & See It On You",
  "See Your Fit",
  "Your fit basics are saved",
  "Add one profile photo to reuse your measurements across PrimeStyleAI products.",
  "Complete Profile",
  "Check your size, then try it on virtually",
  "Get Your Size",
  "Instant fit suggestion",
  "Try It On",
  "See how it looks on you",
  "Find My Size",
  "Takes less than a minute",
  "Upload a full body photo",
  "JPEG, PNG or WebP (max 10MB)",
  "Your photo",
  "Let's find your best fit",
  "Centimeters",
  "Inches",
  "Load saved profile...",
  "HEIGHT",
  "WEIGHT",
  "feet",
  "inches",
  "cm",
  "kg",
  "How to measure",
  "Hide guide",
  "I know my measurements",
  "Not sure? Use height & weight instead",
  "Please fill in height and weight",
  "Please fill in all required fields",
  "Next",
  "Analyzing your size...",
  "SUGGESTED SIZE",
  "NOT SUGGESTED",
  "SUGGESTED",
  "Suggested",
  "Suggested Size",
  "Not Suggested",
  "Your Choice · Not Suggested",
  "YOUR CHOICE · NOT SUGGESTED",
  "your selection · not suggested",
  "suggested",
  "Edit measurements",
  "Size guide",
  "Your fit",
  "Your measure",
  "within range",
  "within range",
  "too tight",
  "tight",
  "a bit tight",
  "too loose",
  "loose",
  "a bit loose",
  "See fit on your photo",
  "Hide visual fit",
  "Analyzing...",
  "Try it on",
  "SIZE CHART",
  "YOUR ESTIMATED MEASUREMENTS",
  "Edit",
  "Download",
  "Size chart",
  "Preparing your image...",
  "Analyzing body proportions...",
  "Matching garment to your photo...",
  "Generating virtual try-on...",
  "Refining details...",
  "Almost there...",
  "Complete!",
  "This usually takes 15-25 seconds",
  "Something went wrong",
  "Try Again",
  "We couldn't find a matching size for you",
  "Please try the manual measurement screen so we can calculate your fit from your measurements.",
  "Try manual measurements",
  "SDK not configured. Please provide an API key.",
  "Please upload a JPEG, PNG, or WebP image.",
  "Please upload a clear full-body photo with both legs and feet visible.",
  "Image must be under 10MB.",
  "Try-on generation failed",
  "Failed to start try-on",
  "No product image found. Please set the product-image attribute.",
  "Powered by",
  "My Profiles",
  "History",
  "No saved profiles yet.",
  "No history yet.",
  "Product",
  "Women's",
  "Men's",
  "Profiles",
  "Women's Profile",
  "Men's Profile",
  "Height",
  "Weight",
  "Chest",
  "Bust",
  "Waist",
  "Hips",
  "Shoulders",
  "Sleeve",
  "Inseam",
  "Neck",
  "Foot",
  "Shoe EU",
  "Shoe US",
  "Shoe UK",
  "Saved",
  "Delete Profile",
  "Shoe size (US)",
  "Shoe size (UK)",
  "Shoe size (EU)",
  "Generating...",
  "Foot length",
  "Size",
  "Try on size",
  "lbs",
  "in",
  "e.g.",
  "Drop your photo here or click to upload",
  "This usually takes 15-20 seconds",
  "Try-on result",
  "Try Another",
  "Back",
  "You",
  "Garment",
  "Your Height",
  "Your Weight",
  "Drag or scroll to set your height",
  "Drag or scroll to set your weight",
  "Enter height and weight first",
  // ── Field labels (FaceSizeView, HeadSizeView, FootSizeView, accessory) ──
  "Lens Width",
  "Bridge",
  "Arm Length",
  "Head Circumference",
  "Face Width",
  "Temple",
  "Around widest point",
  "Foot Length",
  "Chest",
  "Waist",
  "Hips",
  "Shoulder",
  "Sleeve",
  "Neck",
  "Inseam",
  "Thigh",
  "Hip",
  // ── Units ──
  "Centimeters",
  "Inches",
  "Millimetre",
  "Centimetre",
  "Millimeters",
  // ── Fit descriptors ──
  "Tight",
  "Loose",
  "Short",
  "Long",
  "Suggested",
  "Within range",
  "Within range",
  "WITHIN RANGE",
  "✓ Fit",
  // Lowercase variants used inline by fitLabelFn / lengthFitLabelFn
  "tight", "loose", "short", "long",
  "within range",
  "within range",
  "too tight", "a bit tight",
  "too loose", "a bit loose",
  "too short", "a bit short",
  "too long", "a bit long",
  // ── Misc ──
  "Product",
  "preview",
  "Length",
  "Length Adjustment",
  "Regular",
  "Big",
  "Big & Tall",
  // ── Photo step — profile photo preload pill ──
  "Loaded from profile",
  "Clear",
];
